// networkings.js
'use strict';
import { fromJS, Map } from 'immutable';
import {
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_FETCHING_BATCH,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_SUCCESS_BATCH,
  SET_NETWORKING_ERROR,
  SET_NETWORKING_ERROR_BATCH,
  SET_NETWORKING_IDLE,
  LOGOUT,
  CLEAR_NETWORKING_NODES,
  // -- PLOP_PREPEND_REDUCER_ACTION_TYPE --
} from '../ActionTypes.js';

export const getStateTemplate = () => ({
  isFetching: false,
  isFetched: false,
  error: null,
  fetchedTimestamp: null,
  etag: undefined,
});

export const defaultState = fromJS({});

/**
 * Networkings.
 * @module reducer/networkings
 */
const networkings = (state = defaultState, action) => {
  switch (action.type) {
    case SET_NETWORKING_FETCHING:
      return _SET_NETWORKING_FETCHING(action.payload)(state);
    case SET_NETWORKING_SUCCESS:
      return _SET_NETWORKING_SUCCESS(action.payload)(state);
    case SET_NETWORKING_ERROR:
      return _SET_NETWORKING_ERROR(action.payload)(state);
    case SET_NETWORKING_IDLE:
      return _SET_NETWORKING_IDLE(action.payload)(state);
    case SET_NETWORKING_FETCHING_BATCH:
      return _SET_NETWORKING_FETCHING_BATCH(action.payload)(state);
    case SET_NETWORKING_SUCCESS_BATCH:
      return _SET_NETWORKING_SUCCESS_BATCH(action.payload)(state);
    case SET_NETWORKING_ERROR_BATCH:
      return _SET_NETWORKING_ERROR_BATCH(action.payload)(state);
    case LOGOUT:
      return _LOGOUT(action.payload)(state);
    case CLEAR_NETWORKING_NODES:
      return _CLEAR_NETWORKING_NODES(action.payload)(state);
    // -- PLOP_PREPEND_REDUCER_SWITCH_CASE --
    default:
      return state;
  }
};

/**
 * Set networking fetching
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to  by JSONstring for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_FETCHING
 * @param {array} {selectPath} - immutable select path.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_FETCHING =
  ({ selectPath }) =>
  state => {
    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      {
        ...getStateTemplate(),
        isFetching: true,
      }
    );
  };

/**
 * Set networking fetching batch
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to string for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_FETCHING_BATCH
 * @param {array} {selectPaths} - immutable select path.
 * @param {array} {items} - batched items.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_FETCHING_BATCH =
  ({ selectPath, items }) =>
  state => {
    const itemsMap = Map(
      items.map(item => [
        `${item}`,
        {
          ...getStateTemplate(),
          isFetching: true,
        },
      ])
    );

    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      {
        ...itemsMap.toObject(),
      }
    );
  };

/**
 * Set networking success
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to string for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_SUCCESS
 * @param {object} {selectPath} - immutable select path.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_SUCCESS =
  ({ selectPath, etag }) =>
  state => {
    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      {
        isFetching: false,
        isFetched: true,
        error: null,
        fetchedTimestamp: Date.now(),
        etag,
      }
    );
  };

/**
 * Set networking success batch
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to string for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_SUCCESS_BATCH
 * @param {object} {selectPath} - immutable select path.
 * @param {array} {items} - batched items.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_SUCCESS_BATCH =
  ({ selectPath, items }) =>
  state => {
    const itemsMap = Map(
      items.map(item => [
        `${item}`,
        {
          isFetching: false,
          isFetched: true,
          error: null,
          fetchedTimestamp: Date.now(),
        },
      ])
    );

    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      {
        ...itemsMap.toObject(),
      }
    );
  };

/**
 * Set networking error
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to string for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_ERROR
 * @param {object} {selectPath} - immutable select path.
 * @param {Error} {error} - error.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_ERROR =
  ({ selectPath, error }) =>
  state => {
    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      {
        isFetching: false,
        isFetched: true,
        fetchedTimestamp: Date.now(),
        error,
      }
    );
  };

/**
 * Set networking error batch
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to string for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_ERROR_BATCH
 * @param {array} {selectPath} - immutable select path.
 * @param {array} {items} - batched items.
 * @param {Error} {error} - error.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_ERROR_BATCH =
  ({ selectPath, items, error }) =>
  state => {
    const itemsMap = Map(
      items.map(item => [
        `${item}`,
        {
          isFetching: false,
          isFetched: true,
          fetchedTimestamp: Date.now(),
          error,
        },
      ])
    );

    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      {
        ...itemsMap.toObject(),
      }
    );
  };

/**
 * Set networking idle
 * Notice: On SSR rendered html, number in select path (ex: page)
 * will be transformed into string by JSON.stringify. So need to
 * normalize to string for transfering redux store.
 * @kind reducer/actionType
 * @name SET_NETWORKING_IDLE
 * @param {object} {selectPath} - immutable select path.
 * @return {Immutable.Map} New state
 */
const _SET_NETWORKING_IDLE =
  ({ selectPath }) =>
  state => {
    return state.mergeDeepIn(
      selectPath.map(p => `${p}`),
      getStateTemplate()
    );
  };

/**
 * Logout
 * @kind reducer/actionType
 * @name LOGOUT
 * @return {Immutable.Map} New state
 */
const _LOGOUT = () => () => {
  return defaultState;
};

/**
 * Clear networking nodes
 * @kind reducer/actionType
 * @name CLEAR_NETWORKING_NODES
 * @param {object} payload - payload.
 * @param {array} [payload.selectPaths] - select path list.
 * @return {Immutable.Map} New state
 */
const _CLEAR_NETWORKING_NODES =
  ({ selectPaths = [] }) =>
  state => {
    const isValidPath = path => {
      if (path.length === 0) {
        return false;
      }
      return true;
    };
    return selectPaths
      .filter(path => isValidPath(path))
      .reduce((accumulator, path) => {
        return accumulator.setIn(path, fromJS({}));
      }, state);
  };

// -- PLOP_PREPEND_REDUCER_ACTION_HANDLER --

export default networkings;
